import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Divider,
  Button,
  Breadcrumbs,
  Link,
} from "@mui/material";
import { NavigateNext as NavigateNextIcon } from "@mui/icons-material";
import { getData } from "../../Webservice/Webservice";

const rubikfont = {
  fontFamily: "Rubik, sans-serif",
};

const PolicyDetail = () => {
  const { type: typeParam } = useParams();
  const navigate = useNavigate();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);

  const { pathname } = window.location;
  const pathParts = pathname.split('/').filter(Boolean);
  const pathType = pathParts[pathParts.length - 1]; 
  const type = typeParam || pathType;

  const typeMap = {
    'privacy-policy': 'privacy',
    'terms-and-conditions': 'terms',
    'terms': 'terms',
    'refund-policy': 'return_refund',
    'return-policy': 'return_refund',
    'return-refund-policy': 'return_refund',
    'shipping-policy': 'shipping',
    'payment-policy': 'payment',
    'cancellation-policy': 'cancellation',
    'user-account-policy': 'useraccount',
    'user-policy': 'useraccount',
    'liability-limitation': 'liability_limitation'
  };

  const getApiType = (urlType) => typeMap[urlType] || urlType;

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPolicy();
  }, [type, typeParam]);

  const fetchPolicy = async () => {
    setLoading(true);
    try {
      const apiType = getApiType(type);
      const res = await getData(`policy/${apiType}`);
      if (res && res.policies && res.policies.length > 0) {
        setPolicy(res.policies[0]);
      } else {
        setPolicy(null);
      }
    } catch (error) {
      console.error("Error fetching policy:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress sx={{ color: '#C8A054' }} />
      </Box>
    );
  }

  if (!policy) {
    return (
      <Box sx={{ py: 10, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ ...rubikfont }}>Policy not found</Typography>
        <Link href="/" sx={{ mt: 2, display: 'inline-block', color: '#C8A054' }}>Back to Home</Link>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "#ffffff", minHeight: "100vh", py: { xs: 4, md: 8 } }}>
      <Container maxWidth="lg">
        {/* Breadcrumbs for Navigation */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" sx={{ color: "#999" }} />}
          aria-label="breadcrumb"
          sx={{ mb: 4 }}
        >
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate("/")}
            sx={{ cursor: "pointer", fontSize: "14px", fontWeight: 500, color: "#999" }}
          >
            HOME
          </Link>
          <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#222" }}>
            {policy.policyType?.toUpperCase() || 'POLICY'}
          </Typography>
        </Breadcrumbs>

        {/* Professional Header */}
        <Box sx={{ mb: 6, borderBottom: "1px solid #eee", pb: 4 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              fontSize: { xs: "28px", md: "42px" },
              color: "#111",
              ...rubikfont,
              mb: 2
            }}
          >
            {policy.title}
          </Typography>
          {policy.effectiveDate && (
            <Typography sx={{ fontSize: '13px', color: '#666', fontWeight: 500, ...rubikfont }}>
              Last Updated: {new Date(policy.effectiveDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </Typography>
          )}
        </Box>

        {/* Content Section */}
        <Box sx={{ maxWidth: "900px" }}>
          {policy.summary && (
            <Box sx={{ mb: 4, p: 3, bgcolor: "#f8f9fa", borderRadius: 1, borderLeft: "4px solid #C8A054" }}>
              <Typography
                variant="body1"
                sx={{
                  lineHeight: 1.6,
                  color: "#444",
                  fontSize: "16px",
                  fontStyle: "italic",
                  ...rubikfont,
                }}
              >
                {policy.summary}
              </Typography>
            </Box>
          )}

          <Box
            id="policy-content"
            sx={{
              color: "#333",
              lineHeight: 1.8,
              fontSize: "16px",
              "& p": { mb: 3 },
              "& strong": { color: "#000", fontWeight: 600 },
              "& h1, & h2, & h3": { 
                fontSize: "22px",
                color: '#000', 
                fontWeight: 600,
                mb: 2, 
                mt: 4,
              },
              "& ul, & ol": { pl: 4, mb: 3 },
              "& li": { mb: 1 },
              ...rubikfont,
            }}
            dangerouslySetInnerHTML={{ __html: policy.contentHtml }}
          />
          
          <Divider sx={{ my: 8 }} />

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button 
                variant="outlined"
                onClick={() => navigate('/')}
                sx={{ 
                    color: '#222', 
                    borderColor: '#222',
                    px: 4,
                    py: 1.5,
                    borderRadius: 0,
                    fontWeight: 600, 
                    fontSize: '14px',
                    "&:hover": { borderColor: '#000', bgcolor: "#f5f5f5" }
                }}
            >
                RETURN TO SHOP
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PolicyDetail;
